/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import toast from "react-hot-toast";
import { mimeTypeToExtension } from "./getFileMIME";
import { getEnvs } from "state/EnvironmentsContext/EnvContext";
import { getCloudinaryConfig } from "./cloudinary";

const CLOUDINARY_CLOUD_NAME =
  getEnvs((state) => state.cloudinary_cloud_name) || "swtchcc";
const CLOUDINARY_UPLOAD_PRESET =
  getEnvs((state) => state.cloudinary_upload_preset) || "uacvjtnm";

const uploadCloudinary = async (
  payload,
  {
    onUploadStart,
    onUpload,
    onError,
    allowedFileTypes = ["jpeg"],
    isEager = false,
    folderName = "userDocs",
    maxSize = 2,
    uploadPreset = CLOUDINARY_UPLOAD_PRESET,
    middleware,
    cloudinaryType = "raw",
  },
) => {
  const payloadArray = Array.from(payload);
  if (onUploadStart) onUploadStart();
  const newImages = [];
  for (let file of payloadArray) {
    if (allowedFileTypes && allowedFileTypes.length > 0) {
      let formats = allowedFileTypes.map((f) => f.replace(".", ""));
      // "image/jpeg" => jpeg, "application/pdf" => pdf
      const fileExtension = mimeTypeToExtension(file.type);
      // ["image/*"]...etc
      const isFormatsHasAsterisk =
        allowedFileTypes.filter((f) => isFormatIncludesAllTypes(f, file.type))
          .length > 0;

      const isAllowed =
        formats.includes(fileExtension) ||
        formats.includes(file.type) ||
        isFormatsHasAsterisk ||
        typeof file === "string"; //base64 string
      if (!isAllowed) {
        const allowedFormats = formats.join(" / ");
        toast.error(`Only (${allowedFormats}) are allowed`);
        if (onError) onError();
        continue;
      }
    }
    if (typeof file !== "string") {
      const fileSize = getFileSize(file.size);
      if (+fileSize > +maxSize) {
        toast.error(`File size must be less than ${maxSize}MB`);
        if (onError) onError();
        continue;
      }
    }

    const { uploadPreset: finalUploadPreset, uploadUrl } = getCloudinaryConfig(
      file,
      undefined,
      cloudinaryType,
    );
    const formData = new FormData();
    formData.append("file", file);
    formData.append("cloudName", CLOUDINARY_CLOUD_NAME);
    formData.append("upload_preset", finalUploadPreset);
    formData.append("folder", folderName);

    const req = await fetch(uploadUrl, {
      method: "post",
      body: formData,
    });
    if (!req.ok) {
      if (onError) onError();
      toast.error("An error occurred while uploading image");
      continue;
    }
    if (req.ok) {
      const body = await req.json();
      let image;
      if (
        isEager &&
        body.hasOwnProperty("eager") &&
        body.eager.length > 0 &&
        body.eager[0].hasOwnProperty("secure_url")
      ) {
        const { secure_url, bytes } = body.eager[0];

        image = getTransformedUrlBySize(bytes, secure_url, body.secure_url);
      } else if (middleware) {
        const payload = middleware(body);
        if (payload.hasOwnProperty("image")) {
          image = payload.image;
        } else {
          image = body.secure_url;
        }
      } else {
        image = body.secure_url;
      }
      newImages.push({ id: body.asset_id, img: image });
    }
  }

  if (onUpload) onUpload(newImages);
};

export default uploadCloudinary;

const getTransformedUrlBySize = (size, eagerURL, originalURL) => {
  const SIZE_TRANSFORMATIONS = {
    "c_scale,q_50,w_1200": (size) => size > 1 && size <= 2, // 1MB - 2MB
    "c_scale,q_60,w_1400": (size) => size > 0.5 && size <= 1, // 500KB - 1MB
  };
  const fileSizeInMB = getFileSize(size);
  const sizeTransformation = Object.keys(SIZE_TRANSFORMATIONS).find((key) =>
    SIZE_TRANSFORMATIONS[key](fileSizeInMB),
  );
  // it's less than 500kb...so we don't need to transform it.
  if (!sizeTransformation) return eagerURL;
  const injectTransformation = (url) => {
    const [firstPart, secondPart] = url.split("/upload/");
    return `${firstPart}/upload/${sizeTransformation}/${secondPart}`;
  };
  return injectTransformation(originalURL);
};

const getFileSize = (size) => {
  const fileSize = size / 1024 / 1024;
  // 1MB = 1024KB = 1024 * 1024 bytes
  return fileSize.toFixed(2);
};

const isFormatIncludesAllTypes = (format, fileType) => {
  const [type, asterisk] = format.split("/");
  if (!fileType) return false;
  return fileType.includes(type) && asterisk === "*";
};
